/**
 * Notification history drawer - the full, paginated list behind the bell's
 * "View all notifications" link. A point-in-time view that mirrors the bell
 * row exactly (`NotificationItem`) inside the shared `AppDrawer` chrome.
 *
 * Container: the same custom <Paper> + <Slide> side panel as `ExceptionDrawer`
 * (NOT an MUI Drawer) so a11y / layering / mobile backdrop all match the rest
 * of the drawer family.
 */
import React from 'react'
import {
  Backdrop,
  Box,
  Button,
  List,
  Paper,
  Portal,
  Skeleton,
  Slide,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'

import {
  AppDrawerBody,
  AppDrawerFooter,
  AppDrawerHeader,
  AppDrawerShell,
  APP_DRAWER_SHADOW,
} from '../shared/AppDrawer'
import { poolwerxColors } from '../../theme/colors'
import { useNotificationHistory } from '../../hooks/useNotificationHistory'
import NotificationItem from './NotificationItem'

const TEAL = '#007f98'

interface NotificationHistoryDrawerProps {
  open: boolean
  onClose: () => void
  /** Unread count from the bell's `useNotifications` (drives the subtitle). */
  unread: number
}

const NotificationHistoryDrawer: React.FC<NotificationHistoryDrawerProps> = ({
  open,
  onClose,
  unread,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { items, loading, loadingMore, error, hasMore, loadMore, reload, markSeen, markAllSeen } =
    useNotificationHistory(open)

  const showSkeletons = loading && items.length === 0
  const showError = error && !loading && items.length === 0
  const showEmpty = !loading && !error && items.length === 0

  const panel = (
    <Paper
      role="complementary"
      aria-label="Notification history"
      elevation={0}
      sx={{
        position: 'fixed',
        top: 0,
        right: 0,
        height: '100vh',
        // Simple flat list - narrower than the exception drawer's ~580; sits
        // at the shared 520 ceiling. Mobile stays full-width.
        width: isMobile ? '100vw' : 'clamp(440px, 30vw, 520px)',
        zIndex: theme.zIndex.modal + 10,
        borderRadius: 0,
        bgcolor: poolwerxColors.surface.primary,
        borderLeft: `1px solid ${theme.palette.divider}`,
        boxShadow: APP_DRAWER_SHADOW,
      }}
    >
      <AppDrawerShell bg={poolwerxColors.surface.primary}>
        <AppDrawerHeader
          eyebrow="NOTIFICATIONS"
          title="Notification history"
          subtitle={`${unread} unread`}
          onClose={onClose}
        />

        <AppDrawerBody padding={{ p: 0 }}>
          {showSkeletons ? (
            <Box sx={{ px: 2, py: 1 }}>
              {[0, 1, 2, 3, 4].map((i) => (
                <Skeleton
                  key={i}
                  variant="rectangular"
                  height={48}
                  sx={{ borderRadius: 1, my: 1 }}
                />
              ))}
            </Box>
          ) : showError ? (
            <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Couldn't load notifications.
              </Typography>
              <Button
                size="small"
                onClick={() => reload()}
                sx={{ color: TEAL, textTransform: 'none' }}
              >
                Retry
              </Button>
            </Box>
          ) : showEmpty ? (
            <Box sx={{ px: 2, py: 6, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                You're all caught up.
              </Typography>
            </Box>
          ) : (
            <>
              <List
                dense
                disablePadding
                onClick={(e) => {
                  // A row with a deep link closes the drawer as it navigates.
                  if ((e.target as HTMLElement).closest('a')) onClose()
                }}
              >
                {items.map((n) => (
                  <NotificationItem key={n.id} notification={n} onActivate={markSeen} />
                ))}
              </List>
              {hasMore && (
                <Box sx={{ p: 1.5, textAlign: 'center' }}>
                  <Button
                    size="small"
                    onClick={() => loadMore()}
                    disabled={loadingMore}
                    sx={{ color: TEAL, textTransform: 'none' }}
                  >
                    {loadingMore ? 'Loading…' : 'Load earlier'}
                  </Button>
                </Box>
              )}
            </>
          )}
        </AppDrawerBody>

        <AppDrawerFooter>
          <Button
            size="small"
            onClick={() => markAllSeen()}
            disabled={unread === 0}
            sx={{ color: TEAL, textTransform: 'none' }}
          >
            Mark all read
          </Button>
        </AppDrawerFooter>
      </AppDrawerShell>
    </Paper>
  )

  return (
    <Portal>
      {isMobile && (
        <Backdrop open={open} onClick={onClose} sx={{ zIndex: theme.zIndex.modal + 9 }} />
      )}
      <Slide direction="left" in={open} mountOnEnter unmountOnExit>
        {panel}
      </Slide>
    </Portal>
  )
}

export default NotificationHistoryDrawer
